import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import ThemeChanger from '../Components/ThemeChanger';
import AddTopicModal from './../Components/admin/AddTopicModal';
import AddSubTopicModal from './../Components/admin/AddSubTopicModal';
import AddModuleModal from './../Components/admin/AddModuleModal';
import api from './../api';
import Modal from './../Components/admin/Modal';
import Card from './../Components/admin/Card';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export default function AdminDashboard() {
    const [editUser, setEditUser] = useState(null);
    const [users, setUsers] = useState(null);
    const [modal, setModal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [allTopics, setAllTopics] = useState(null);
    const [userToDelete, setUserToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const navigate = useNavigate();

    const [currentPage, setCurrentPage] = useState(1);
    const [usersPerPage] = useState(8); 

    const fetchData = () => {
        setLoading(true);
        api.get('/admin/get-all-user')
            .then((res) => {
                const response = res.data;
                if (response.statusCode === 200) {
                    setUsers(response.data);
                } else {
                    navigate('/');
                }
            })
            .catch((error) => {
                console.error('Error fetching users:', error);
                // Optionally handle error state here
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const fetchTopic = () => {
        api.get('/admin/get-all-topics').then((res) => {
            const response = res.data;
            if (response.statusCode === 200) {
                setAllTopics(response.data);
            } else {
                navigate('/');
            }
        });
    };

    useEffect(() => {
        fetchTopic();
        fetchData();
    }, []);

    const handleEdit = (user) => {
        setEditUser(user);
    };

    const handleClose = () => {
        setEditUser(null);
        fetchData();
    };

    const handleCloseModal = ({ modalName }) => {
        if (modalName && modalName !== 'null') {
            fetchTopic();
        }
        setModal(null);
    };

    const handleDelete = (userId) => {
        setUserToDelete(userId);
    };

    const confirmDelete = async () => {
        if (!userToDelete) return;

        setIsDeleting(true);
        try {
            const response = await api.delete(
                `/admin/delete-user/${userToDelete}`
            );
            if (response.data.statusCode === 200) {
                // Remove the user from the list
                setUsers(users.filter((user) => user._id !== userToDelete));
                // Show success message (optional)
            } else {
                // Handle error (optional)
                console.error('Failed to delete user:', response.data.message);
            }
        } catch (error) {
            console.error('Error deleting user:', error);
        } finally {
            setUserToDelete(null);
            setIsDeleting(false);
        }
    };

    const cancelDelete = () => {
        setUserToDelete(null);
    };

    // Get current users for pagination
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsers = users
        ? users.slice(indexOfFirstUser, indexOfLastUser)
        : [];

    // Calculate total pages
    const totalPages = users ? Math.ceil(users.length / usersPerPage) : 0;

    // Change page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const nextPage = () =>
        setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

    return (
      <div className="min-h-screen bg-base-200 p-4">
        {/* Header with navigation and action buttons */}
        <div className="fixed top-0 left-0 right-0 z-10 px-4 py-3 bg-base-100 shadow-md">
          <div className="w-full max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-2">
              <button
                className="btn btn-info btn-outline btn-sm"
                onClick={() => navigate('/')}
              >
                Home
              </button>
            </div>

            <div className="flex gap-3">
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setModal('topic')}
              >
                Add Topic
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setModal('subTopic')}
              >
                Add Subtopic
              </button>
              <button
                className="btn btn-accent btn-sm"
                onClick={() => setModal('module')}
              >
                Add Module
              </button>
              <button
                className="btn btn-info btn-sm"
                onClick={() => navigate('/admin/all-topics')}
              >
                All Topics
              </button>
              <button
                className="btn btn-error btn-sm"
                onClick={() => navigate('/admin/report-question')}
              >
                Report
              </button>
            </div>

            <div>
              <ThemeChanger />
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center pt-16">
          <div className="w-full max-w-7xl">
            {loading ? (
              <div className="flex justify-center items-center min-h-[400px]">
                <div className="flex flex-col items-center gap-4">
                  <span className="loading loading-spinner loading-lg text-primary"></span>
                  <p className="text-base-content/70">Loading users...</p>
                </div>
              </div>
            ) : users && users.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 place-items-center">
                  {currentUsers.map((user) => (
                    <Card
                      key={user._id}
                      user={user}
                      handleEdit={handleEdit}
                      handleDelete={handleDelete} // Pass the delete handler
                    />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center mt-8">
                    <div className="join">
                      <button
                        className="join-item btn btn-sm"
                        onClick={prevPage}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft size={16} />
                      </button>

                      {[...Array(totalPages)].map((_, index) => (
                        <button
                          key={index}
                          className={`join-item btn btn-sm ${
                            currentPage === index + 1 ? 'btn-active' : ''
                          }`}
                          onClick={() => paginate(index + 1)}
                        >
                          {index + 1}
                        </button>
                      ))}

                      <button
                        className="join-item btn btn-sm"
                        onClick={nextPage}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex justify-center items-center min-h-[400px]">
                <div className="text-center">
                  <p className="text-xl text-base-content/70 mb-2">
                    No users found
                  </p>
                  <button
                    onClick={fetchData}
                    className="btn btn-primary btn-sm"
                  >
                    Refresh
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        {editUser && <Modal onClose={handleClose} user={editUser} />}
        {modal === 'topic' && <AddTopicModal handleClose={handleCloseModal} />}
        {modal === 'subTopic' && (
          <AddSubTopicModal
            handleClose={handleCloseModal}
            allTopics={allTopics}
          />
        )}
        {modal === 'module' && (
          <AddModuleModal
            handleClose={handleCloseModal}
            allTopics={allTopics}
          />
        )}

        {/* Confirmation Modal for Deletion */}
        {userToDelete && (
          <div className="fixed inset-0 bg-base-200/50 z-20 flex items-center justify-center">
            <div className="bg-base-100 p-6 rounded-lg shadow-xl max-w-md w-full">
              <h3 className="text-lg font-bold mb-4">Confirm Deletion</h3>
              <p className="mb-6">
                Are you sure you want to delete this user? This action cannot be
                undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  className="btn btn-ghost"
                  onClick={cancelDelete}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-error"
                  onClick={confirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <span className="loading loading-spinner loading-sm"></span>
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
}
